/*
 * @Descripttion: 
 * @Author: zhaozheng
 * @Version: 0.0.1
 * @Date: 2025-10-21 12:59:34
 * @LastEditors: zhaozheng
 * @LastEditTime: 2025-11-21 19:20:48
 */
import React, { VFC, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { css } from '@emotion/css';
import { TCanvas } from './three/TCanvas';
import { Reveal } from './Reveal';
import { P1Home } from './sections/P1Home';
import { P2Section } from './sections/P2Section';
import { P3Section } from './sections/P3Section';
import { P4Section } from './sections/P4Section';
import { P5Section } from './sections/P5Section';
import { P6Section } from './sections/P6Section';
import { P7Section } from './sections/P7Section';
import { FormModal } from './FormModal';
import { OpeningLoading } from './OpeningLoading';
import { Footer } from './Footer';
import { PALETTE_HEX } from '../constants/palettes';
import { BgControlContext, BgPalette } from './BgControlContext';
import logo from '../assets/images/logo.png';

const sections = [P1Home, P2Section, P3Section, P4Section, P5Section, P6Section, P7Section];

export const App: VFC = () => {
  const p3RevealRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setVh();
    window.addEventListener('resize', setVh);
    return () => window.removeEventListener('resize', setVh);
  }, []);

  const palettes = useMemo(() => PALETTE_HEX, []);
  const [bgState, setBgState] = useState({ base: 0, next: 0, progress: 0 });
  const [overridePalette, setOverridePalette] = useState<BgPalette | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scrollDir, setScrollDir] = useState(1);
  const lastScroll = useRef(0);
  const lastDir = useRef(1);

  useEffect(() => {
    const handleScroll = () => {
      const vh = window.innerHeight || 1;
      const raw = window.scrollY / vh;
      const base = Math.min(Math.max(Math.floor(raw), 0), palettes.length - 1);
      const next = Math.min(base + 1, palettes.length - 1);
      const progress = next === base ? 0 : Math.min(Math.max(raw - base, 0), 1);

      setBgState(prev => (
        prev.base === base && prev.next === next && prev.progress === progress
          ? prev
          : { base, next, progress }
      ));

      const dir = raw > lastScroll.current ? 1 : raw < lastScroll.current ? -1 : lastDir.current;
      lastScroll.current = raw;
      if (dir !== lastDir.current) {
        lastDir.current = dir;
        setScrollDir(dir);
      }
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [palettes.length]);

  const paletteA = palettes[bgState.base];
  const paletteB = palettes[bgState.next];
  const blend = bgState.progress;

  const effectivePaletteA = overridePalette ?? paletteA;
  const effectivePaletteB = overridePalette ?? paletteB;
  const effectiveBlend = overridePalette ? 0 : blend;

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <div className={styles.container}>
      {loading && <OpeningLoading onComplete={() => setLoading(false)} />}
      <TCanvas paletteA={effectivePaletteA} paletteB={effectivePaletteB} blend={effectiveBlend} modalOpen={modalOpen} />

      {/* Logo */}
      <div className={styles.logo}>
        <img src={logo} alt="Token Map 词元映射" />
      </div>

      <BgControlContext.Provider value={{ setOverridePalette, setModalOpen }}>
        <main className={styles.stack}>
          {sections.map((Section, idx) => {
            // P3Section (idx === 2) 使用特殊的ref进行滚动劫持
            if (idx === 2) {
              return (
                <div key={idx} style={{ pointerEvents: 'auto' }}>
                  <Reveal direction={scrollDir} ref={p3RevealRef}>
                    <P3Section containerRef={p3RevealRef as React.RefObject<HTMLElement>} />
                  </Reveal>
                </div>
              );
            }
            return (
              <Reveal key={idx} direction={scrollDir}>
                <Section />
              </Reveal>
            );
          })}
          <Footer />
        </main>
      </BgControlContext.Provider>

      <FormModal isOpen={modalOpen} onClose={handleCloseModal} />
    </div>
  );
};

const styles = {
  container: css`
    position: relative;
    width: 100vw;
    min-height: calc(var(--vh, 1vh) * 100);
    overflow-x: hidden;
    overflow-y: visible;
  `,
  logo: css`
    position: fixed;
    top: 30px;
    left: 40px;
    z-index: 1000;
    pointer-events: none;
    
    img {
      height: 53px;
      width: auto;
      display: block;
    }
    
    @media (max-width: 768px) {
      position: absolute;
      top: 20px;
      left: 20px;
      img {
        height: 45px;
      }
    }
  `,
  stack: css`
    pointer-events: none;
    position: relative;
    z-index: 10;
    display: flex;
    flex-direction: column;
    gap: 20px;
  `,
};

